import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SharedService } from '../../shared/shared.service';
import { UserOption, UserResponse } from '../models/user.models';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // Base URL for API calls
  private baseUrl = `${environment.fastApiUrl}`;
  private userOptionsCache$?: Observable<UserOption[]>;

  constructor(private sharedService: SharedService) {}

  /**
   * Get list of users
   * @param isActive Filter by active status (default: true)
   * @param includeDeleted Include soft-deleted users (default: false)
   */
  getUsers(isActive: boolean = true, includeDeleted: boolean = false): Observable<UserResponse[]> {
    let url = `${this.baseUrl}/auth/users?`;
    const params: string[] = [];

    if (isActive !== undefined) params.push(`is_active=${isActive}`);
    if (includeDeleted) params.push(`include_deleted=${includeDeleted}`);

    url += params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Cached user options for dropdowns/menus (single HTTP request per session).
   */
  getUserOptions(isActive: boolean = true, forceRefresh = false): Observable<UserOption[]> {
    if (forceRefresh) {
      this.userOptionsCache$ = undefined;
    }
    if (!this.userOptionsCache$) {
      this.userOptionsCache$ = this.getUsers(isActive).pipe(
        map((users) => this.mapUsersToOptions(users || [])),
        shareReplay(1)
      );
    }
    return this.userOptionsCache$;
  }

  /**
   * Helper method to map users to dropdown options
   */
  mapUsersToOptions(users: UserResponse[]): UserOption[] {
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
      roleName: user.role_name,
      profilePictureUrl: user.profile_picture_url,
      isActive: user.is_active,
    }));
  }
}
